import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import get from 'lodash/get';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage, defineMessages } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as ApiActions from 'actions/api';
import Layout from 'components/Layout';
import Press, { quotes } from 'constants/Press';
import progress from 'utils/progress';
import 'styles/buttons.scss';
import '../styles/clearing-request.scss';
const messages = defineMessages({
    title: 'Hi {firstname}, you asked us to delete the Sweatcoin account linked to mobile number {masked_number} and email {masked_email}.',
    description: '{hasBalance, select, true {If you go ahead, your remaining balance of {balance, number, amount_grouping} sweatcoins will be transferred to the Sweatcoin Treasury. } other {}}A deleted account can’t be restored. Can we do anything to persuade you to stay? If not, thanks for downloading our app and giving us a go.',
    cancel: 'Keep my account',
    confirm: 'Delete my account',
    confirmed: '{hasDate, select, true {Your account is scheduled for deletion on {clearingAt, date, long} at {clearingAt, time, short}.} other {Your account is scheduled for deletion.}}',
    confirmed_undo: 'Changed your mind? You can still keep your account.',
    invalid: 'This link is no longer active. It may have expired, or the request was already cancelled or completed. If you still want to delete your account, you can send a new request from the Sweatcoin app.',
    cancel_success: 'Phew!{br}Your account and your sweatcoins are now safe.{br}Happy Sweatcoining for many years to come!!!',
    cancel_error: 'Something went wrong and we couldn’t cancel your deletion request. Please try again.',
    confirm_error: 'Something went wrong and we couldn’t delete your account. Please try again.',
    load_error: 'Something went wrong and we couldn’t load your deletion request. Please reload the page.',
});
const GONE = [404, 410];
const clearingRequestSelector = (state) => state.clearingRequest;
const mapStateToProps = createStructuredSelector({
    clearingRequest: clearingRequestSelector,
});
class ClearingRequest extends Component {
    constructor() {
        super(...arguments);
        this.state = {};
        this._renderContent = () => {
            const { clearingRequest } = this.props;
            const { view } = this.state;
            if (view === 'cancelled') {
                return this._renderMessage(messages.cancel_success);
            }
            if (view === 'invalid') {
                return this._renderMessage(messages.invalid);
            }
            if (!clearingRequest) {
                return null;
            }
            if (clearingRequest.failed) {
                return this._renderMessage(clearingRequest.status === 404 ? messages.invalid : messages.load_error);
            }
            if (clearingRequest.clearing_at > 0 || clearingRequest.confirmed) {
                return this._renderConfirmed();
            }
            return this._renderRequest();
        };
        this._renderMessage = (message) => (_jsx("p", { children: _jsx(FormattedMessage, Object.assign({}, message)) }));
        this._renderRequest = () => {
            const { balance, firstname, masked_email, masked_number } = this.props.clearingRequest;
            return (_jsxs("div", { children: [_jsx("p", { children: _jsx(FormattedMessage, Object.assign({}, messages.title, { values: { firstname, masked_number, masked_email } })) }), _jsx("p", { children: _jsx(FormattedMessage, Object.assign({}, messages.description, { values: { balance, hasBalance: balance > 0 } })) }), _jsxs("div", { className: "clearing-request__actions", children: [this._renderKeepButton(), _jsx("button", { className: "button button--primary", disabled: this.state.busy, onClick: () => {
                                    void this._handleConfirm();
                                }, children: _jsx(FormattedMessage, Object.assign({}, messages.confirm)) })] }), this._renderError()] }));
        };
        this._renderConfirmed = () => {
            const { clearing_at } = this.props.clearingRequest;
            return (_jsxs("div", { children: [_jsx("p", { children: _jsx(FormattedMessage, Object.assign({}, messages.confirmed, { values: { hasDate: clearing_at > 0, clearingAt: clearing_at * 1000 } })) }), _jsx("p", { children: _jsx(FormattedMessage, Object.assign({}, messages.confirmed_undo)) }), _jsx("div", { className: "clearing-request__actions", children: this._renderKeepButton() }), this._renderError()] }));
        };
        this._renderKeepButton = () => (_jsx("button", { className: "button button--opaque", disabled: this.state.busy, onClick: () => {
                void this._handleCancel();
            }, children: _jsx(FormattedMessage, Object.assign({}, messages.cancel)) }));
        this._renderError = () => this.state.error && (_jsx("p", { className: "clearing-request__error", children: _jsx(FormattedMessage, Object.assign({}, messages[this.state.error])) }));
        this._renderPressEntry = (model, index) => (_jsx(FormattedMessage, Object.assign({}, quotes[model.quoteKey], { children: (quote) => (_jsx("a", { href: model.link, target: "_blank", rel: "noreferrer noopener", className: "clearing-request__press__entry", title: quote, children: model.sourceBlack }, `press-entry-${index}`)) })));
        this._token = () => queryString.parse(get(this.props, 'location.search', '')).confirmation_token;
        this._request = async (action) => {
            progress.increment();
            this.setState({ busy: true, error: null });
            const result = await action(this._token());
            progress.decrement();
            return result && result.error ? get(result, 'payload.status', 0) : 200;
        };
        this._handleConfirm = async () => {
            const status = await this._request(this.props.confirmClearingRequest);
            if (status === 200 || status === 409) {
                await this.props.showClearingRequest(this._token(), true);
                this.setState({ busy: false });
            }
            else if (GONE.includes(status)) {
                this.setState({ busy: false, view: 'invalid' });
            }
            else {
                this.setState({ busy: false, error: 'confirm_error' });
            }
        };
        this._handleCancel = async () => {
            const status = await this._request(this.props.cancelClearingRequest);
            if (status === 200 || status === 409) {
                this.setState({ busy: false, view: 'cancelled' });
            }
            else if (GONE.includes(status)) {
                this.setState({ busy: false, view: 'invalid' });
            }
            else {
                this.setState({ busy: false, error: 'cancel_error' });
            }
        };
    }
    static async fetchData(state, store, { location }) {
        await store.dispatch(ApiActions.showClearingRequest(get(location, 'query.confirmation_token')));
    }
    // The outcome of a press lives in this component, so a later visit (e.g. browser Back) must read the request again.
    componentWillUnmount() {
        this.props.resetClearingRequest();
    }
    render() {
        return (_jsxs(Layout, { header: false, className: "clearing-request", children: [_jsx(Helmet, { meta: [{ name: 'event-id', content: 'clearing-request' }] }), _jsxs(Layout.Container, { children: [_jsx("section", { className: "clearing-request__content", children: this._renderContent() }), _jsx("div", { className: "clearing-request__press", children: Press.map(this._renderPressEntry) })] })] }));
    }
}
ClearingRequest.contextTypes = {
    router: PropTypes.object,
};
export default connect(mapStateToProps, ApiActions)(ClearingRequest);
